// ============================================================
// auth-screen.ts — Login / Register / Guest auth screen
// DOM-based UI matching the existing military theme.
// ============================================================

// Singleton module — a single auth screen instance is shared across the app.
// The module-level state is intentional; only one auth screen exists at a time.
let container: HTMLDivElement | null = null;
let successCallback: ((token: string, displayName: string) => void) | null = null;

// ── Internal state ──────────────────────────────────────────

let loading = false;

// ── Style injection (once) ──────────────────────────────────

let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
    #auth-screen {
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      background: linear-gradient(160deg, #0d0e0a 0%, #1a1e14 40%, #22261a 70%, #111510 100%);
      display: flex; justify-content: center; align-items: center; flex-direction: column;
      z-index: 200;
      font-family: 'Courier New', monospace;
    }
    #auth-screen::before {
      content: '';
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      background:
        repeating-linear-gradient(
          0deg,
          transparent,
          transparent 2px,
          rgba(0,0,0,0.05) 2px,
          rgba(0,0,0,0.05) 4px
        );
      pointer-events: none;
    }

    .auth-title {
      font-size: 56px; color: #e94560;
      text-transform: uppercase; letter-spacing: 12px; font-weight: bold;
      margin-bottom: 6px;
      text-shadow: 0 0 30px rgba(233,69,96,0.3), 2px 2px 0 #222;
    }
    .auth-subtitle {
      font-size: 14px; color: #5a6a4a; letter-spacing: 6px;
      text-transform: uppercase; margin-bottom: 36px;
    }

    .auth-form {
      display: flex; flex-direction: column; align-items: center; gap: 14px;
      width: min(340px, calc(100vw - 32px)); position: relative; z-index: 1;
    }

    .auth-input {
      width: 100%; padding: 12px 16px; font-size: 16px;
      background: rgba(30, 34, 26, 0.85);
      color: #c8c090; border: 1px solid #3a4a2a;
      font-family: 'Courier New', monospace;
      letter-spacing: 1px; outline: none;
      transition: border-color 0.15s;
    }
    .auth-input::placeholder { color: #4a5a3a; }
    .auth-input:focus { border-color: #6a7a4a; }

    .auth-btn {
      width: 100%; padding: 14px 0; font-size: 16px;
      background: rgba(80, 90, 60, 0.6);
      color: #c8c090; border: 1px solid #5a6a3a;
      cursor: pointer; text-transform: uppercase;
      letter-spacing: 4px; transition: all 0.15s;
      font-family: 'Courier New', monospace; font-weight: bold;
    }
    .auth-btn:hover:not(:disabled) {
      background: rgba(100, 110, 70, 0.7);
      color: #e0d8a0; border-color: #8a9a5a;
    }
    .auth-btn:disabled {
      opacity: 0.4; cursor: not-allowed;
    }

    .auth-btn-accent {
      background: rgba(233, 69, 96, 0.25);
      border-color: rgba(233, 69, 96, 0.5);
      color: #e94560;
    }
    .auth-btn-accent:hover:not(:disabled) {
      background: rgba(233, 69, 96, 0.4);
      border-color: rgba(233, 69, 96, 0.7);
      color: #ff6b8a;
    }

    .auth-divider {
      display: flex; align-items: center; gap: 12px;
      width: 100%; color: #3a4a2a; font-size: 12px;
      letter-spacing: 2px; text-transform: uppercase;
      margin: 4px 0;
    }
    .auth-divider::before,
    .auth-divider::after {
      content: ''; flex: 1; height: 1px; background: #3a4a2a;
    }

    .auth-error {
      width: 100%; padding: 10px 14px; font-size: 12px;
      background: rgba(233, 69, 96, 0.15);
      border: 1px solid rgba(233, 69, 96, 0.35);
      color: #e94560; letter-spacing: 1px;
      display: none;
    }

    .auth-loading {
      display: none; color: #5a6a4a; font-size: 13px;
      letter-spacing: 2px; text-transform: uppercase;
    }
    .auth-loading.visible { display: block; }

    .auth-hint {
      margin-top: 12px; color: #3a4a30; font-size: 12px;
      letter-spacing: 1px;
    }
  `;
  document.head.appendChild(style);
}

// ── DOM construction ────────────────────────────────────────

function build(): HTMLDivElement {
  injectStyles();

  const el = document.createElement('div');
  el.id = 'auth-screen';

  el.innerHTML = `
    <div class="auth-title">Counter Pump</div>
    <div class="auth-subtitle">Tactical Deathmatch</div>
    <div class="auth-form">
      <input class="auth-input" id="auth-display-name" type="text"
             placeholder="Display Name" maxlength="24"
             autocomplete="username" autocapitalize="off" autocorrect="off"
             spellcheck="false" inputmode="text" />
      <input class="auth-input" id="auth-wallet" type="text"
             placeholder="Solana wallet address (optional)" autocomplete="off"
             autocapitalize="off" autocorrect="off" spellcheck="false"
             inputmode="text" maxlength="44" />
      <div class="auth-error" id="auth-error"></div>
      <button class="auth-btn auth-btn-accent" id="auth-login-btn">Continue</button>
      <div class="auth-loading" id="auth-loading">Connecting...</div>
      <div class="auth-hint">Add a wallet to keep your stats across sessions</div>
    </div>
  `;

  // Wire events after inserting markup
  const nameInput = el.querySelector('#auth-display-name') as HTMLInputElement | null;
  const walletInput = el.querySelector('#auth-wallet') as HTMLInputElement | null;
  const loginBtn = el.querySelector('#auth-login-btn') as HTMLButtonElement | null;

  if (loginBtn && nameInput && walletInput) {
    loginBtn.addEventListener('click', () => handleLogin(nameInput, walletInput));
  }

  // Enter submits from either field.
  if (walletInput && nameInput) {
    walletInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') handleLogin(nameInput, walletInput);
    });
    nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') handleLogin(nameInput, walletInput);
    });
  }

  return el;
}

// ── Helpers ─────────────────────────────────────────────────

function setLoading(state: boolean): void {
  loading = state;
  if (!container) return;

  const loginBtn = container.querySelector('#auth-login-btn') as HTMLButtonElement;
  const loadingEl = container.querySelector('#auth-loading') as HTMLElement;

  loginBtn.disabled = state;
  loadingEl.classList.toggle('visible', state);
}

function showError(msg: string): void {
  if (!container) return;
  const errorEl = container.querySelector('#auth-error') as HTMLElement;
  errorEl.textContent = msg;
  errorEl.style.display = 'block';
}

function clearError(): void {
  if (!container) return;
  const errorEl = container.querySelector('#auth-error') as HTMLElement;
  errorEl.style.display = 'none';
}

function validateDisplayName(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed.length === 0) return 'Display name is required';
  // Server requires 3-16; keep client constraint identical so the round trip
  // never returns a 400 the user can't predict.
  if (trimmed.length < 3) return 'Name must be at least 3 characters';
  if (trimmed.length > 16) return 'Name must be 16 characters or fewer';
  return null;
}

// Solana base58 alphabet: omit 0, O, I, l. Length 32-44 covers every
// realistic ed25519-pubkey encoding. Mirror of the server-side check so
// a malformed wallet is caught locally before the network round trip.
const SOLANA_ADDRESS_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

// In production, the game client (play.counterpump.com) and the API
// (game.counterpump.com) live on different hosts, so /api/* must be an
// absolute URL. In dev, fall back to relative so Vite's proxy works.
function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
}

function validateWallet(wallet: string): string | null {
  if (wallet.length === 0) return null; // optional
  if (!SOLANA_ADDRESS_RE.test(wallet)) return 'Invalid Solana wallet address';
  return null;
}

// ── Auth handlers ───────────────────────────────────────────

async function handleLogin(nameInput: HTMLInputElement, walletInput: HTMLInputElement): Promise<void> {
  // Dismiss the iOS/Android on-screen keyboard before we kick off the network
  // round trip — without this the keyboard hangs around obscuring the error/
  // loading states.
  nameInput.blur();
  walletInput.blur();

  if (loading) return;
  clearError();

  const displayName = nameInput.value.trim();
  const wallet = walletInput.value.trim();

  const nameErr = validateDisplayName(displayName);
  if (nameErr) { showError(nameErr); return; }

  const walletErr = validateWallet(wallet);
  if (walletErr) { showError(walletErr); return; }

  setLoading(true);

  try {
    const payload: { display_name: string; wallet_address?: string } = { display_name: displayName };
    if (wallet) payload.wallet_address = wallet;

    const res = await fetch(`${apiBase()}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: 'Authentication failed' }));
      throw new Error(body.error || `Server error (${res.status})`);
    }

    const data = await res.json() as { token: string };
    successCallback?.(data.token, displayName);
  } catch (err) {
    showError(err instanceof Error ? err.message : 'Authentication failed');
  } finally {
    setLoading(false);
  }
}

// ── Public API ──────────────────────────────────────────────

/** Show the auth screen (creates it if first call). */
export function showAuthScreen(): void {
  if (!container) {
    container = build();
  }
  if (!container.parentElement) {
    document.body.appendChild(container);
  }
  container.style.display = 'flex';

  // Auto-focus the display name input
  const nameInput = container.querySelector('#auth-display-name') as HTMLInputElement | null;
  nameInput?.focus();
}

/** Hide the auth screen. */
export function hideAuthScreen(): void {
  if (container) {
    container.style.display = 'none';
  }
}

/**
 * Register a callback that fires when authentication succeeds.
 * The callback receives the JWT token and the display name.
 */
export function onAuthSuccess(callback: (token: string, displayName: string) => void): void {
  successCallback = callback;
}
